import React, { useState, useEffect } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { 
  ArrowLeft, Brain, TrendingUp, AlertTriangle, Target, Settings,
  Loader2, CheckCircle2, XCircle, BarChart3, PieChart, Activity
} from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/learning`;

export default function MachineLearning() {
  const [loading, setLoading] = useState(false);
  const [insights, setInsights] = useState(null);
  const [failureAnalysis, setFailureAnalysis] = useState(null);
  const [sectorPerformance, setSectorPerformance] = useState(null);
  const [recommendations, setRecommendations] = useState(null);
  const [settings, setSettings] = useState(null);
  const [days, setDays] = useState(30);
  
  // NEW: Learned patterns and rules
  const [learnedPatterns, setLearnedPatterns] = useState(null);
  const [learnedRules, setLearnedRules] = useState(null);
  const [runningAdvancedLearning, setRunningAdvancedLearning] = useState(false);
  const [sectorPerformanceDetailed, setSectorPerformanceDetailed] = useState(null);
  
  // ML Analysis Stocks (Job 19)
  const [mlAnalysisStocks, setMlAnalysisStocks] = useState(null);
  const [mlSectorPerformance, setMlSectorPerformance] = useState(null);

  useEffect(() => {
    fetchData();
    fetchLearnedPatterns();
    fetchMLAnalysisStocks();
  }, [days]);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      const today = new Date().toISOString().split('T')[0];
      
      // Fetch insights from trade-history endpoint (same as History page for consistency)
      const historyRes = await axios.get(`${BACKEND_URL}/api/ai/trade-history?days=${days * 30}`); // Convert days to approximate range
      
      // Transform trade-history data to insights format
      const transformedInsights = {
        total_predictions: historyRes.data.total || 0,
        correct_predictions: historyRes.data.successful || 0,
        wrong_predictions: historyRes.data.not_successful || 0,
        accuracy_pct: historyRes.data.successful_pct || 0,
        // Calculate critical failures from the trades
        predicted_up_went_down: historyRes.data.trades?.filter(t => 
          t.strategy === 'BUY' && t.evaluation_result === 'LOSS'
        ).length || 0,
        predicted_down_went_up: historyRes.data.trades?.filter(t => 
          t.strategy === 'SHORT' && t.evaluation_result === 'LOSS'
        ).length || 0,
      };
      
      const [failureRes, sectorRes, recsRes, settingsRes] = await Promise.all([
        axios.get(`${API}/failure-analysis?days=30`),
        axios.get(`${API}/sector-performance?days=${days}`),
        axios.get(`${API}/sector-recommendations?date=${today}`),
        axios.get(`${API}/settings`),
      ]);
      
      setInsights(transformedInsights);
      setFailureAnalysis(failureRes.data);
      setSectorPerformance(sectorRes.data);
      setRecommendations(recsRes.data);
      setSettings(settingsRes.data);
    } catch (err) {
      console.error("ML data fetch error:", err);
      toast.error("Failed to fetch machine learning data");
    } finally {
      setLoading(false);
    }
  };
  
  const fetchLearnedPatterns = async () => {
    try {
      const [patternsRes, rulesRes, sectorPerfRes] = await Promise.all([
        axios.get(`${API}/learned-patterns`),
        axios.get(`${API}/learned-rules`),
        axios.get(`${API}/sector-performance-detailed`),
      ]);
      
      setLearnedPatterns(patternsRes.data);
      setLearnedRules(rulesRes.data);
      setSectorPerformanceDetailed(sectorPerfRes.data);
    } catch (err) {
      console.error("Learned patterns fetch error:", err);
    }
  };

  const fetchMLAnalysisStocks = async () => {
    try {
      const cacheBuster = Date.now();
      // Fetch ML-specific stocks from ml-analysis endpoint (20 stocks/day for learning)
      const [stocksRes, perfRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/ai/ml-analysis/stocks?days=${days}&_t=${cacheBuster}`, {
          headers: { 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' }
        }),
        axios.get(`${BACKEND_URL}/api/ai/ml-analysis/sector-performance?days=${days}&_t=${cacheBuster}`, {
          headers: { 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' }
        })
      ]);
      
      setMlAnalysisStocks(stocksRes.data);
      setMlSectorPerformance(perfRes.data);
    } catch (err) {
      console.error("Error fetching ML analysis stocks:", err);
    }
  };

  
  const runAdvancedLearning = async () => {
    try {
      setRunningAdvancedLearning(true);
      toast.info("Starting advanced pattern learning...");
      
      const res = await axios.post(`${API}/run-advanced-learning?lookback_days=30`);
      
      toast.success(`Learning complete! Found ${res.data.result.patterns_found} patterns, generated ${res.data.result.rules_generated} rules`);
      
      // Refresh patterns
      await fetchLearnedPatterns();
    } catch (err) {
      console.error("Advanced learning error:", err);
      toast.error("Failed to run advanced learning");
    } finally {
      setRunningAdvancedLearning(false);
    }
  };

  const updateSettings = async (updates) => {
    try {
      const res = await axios.put(`${API}/settings`, updates);
      setSettings(res.data);
      toast.success("Settings updated successfully");
    } catch (err) {
      console.error("Settings update error:", err);
      toast.error("Failed to update settings");
    }
  };

  const backfillData = async () => {
    try {
      setLoading(true);
      const res = await axios.post(`${API}/backfill-from-history`);
      toast.success(`Backfilled ${res.data.predictions_added} predictions and ${res.data.outcomes_added} outcomes`);
      // Refresh data
      await fetchData();
    } catch (err) {
      console.error("Backfill error:", err);
      toast.error("Failed to backfill learning data");
    } finally {
      setLoading(false);
    }
  };

  const formatFailureReason = (reason) => {
    return reason.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
  };

  if (loading && !insights) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <Link to="/">
              <Button variant="ghost" size="sm" className="gap-2">
                <ArrowLeft className="h-4 w-4" />
                Back to Dashboard
              </Button>
            </Link>
          </div>
          <h1 className="text-3xl font-bold flex items-center gap-3">
            <Brain className="h-8 w-8 text-[hsl(var(--primary))]" />
            Machine Learning Engine
          </h1>
          <p className="text-muted-foreground mt-1">
            AI-powered learning from prediction outcomes
          </p>
        </div>
        <div className="flex items-center gap-2">
          {(!insights || insights.total_predictions === 0) && (
            <Button onClick={backfillData} disabled={loading} variant="outline">
              {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Load Historical Data
            </Button>
          )}
          <Button onClick={fetchData} disabled={loading}>
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Refresh"}
          </Button>
        </div>
      </div>

      <Tabs defaultValue="overview" className="space-y-6">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="failures">Failure Analysis</TabsTrigger>
          <TabsTrigger value="sectors">Sector Performance</TabsTrigger>
          <TabsTrigger value="recommendations">Recommendations</TabsTrigger>
          <TabsTrigger value="learned-patterns">Learned Patterns</TabsTrigger>
          <TabsTrigger value="ml-stocks">ML Analysis Stocks</TabsTrigger>
          <TabsTrigger value="settings">Settings</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold">Learning Progress</h2>
            <div className="flex items-center gap-2">
              <Label>Days:</Label>
              <Input 
                type="number" 
                value={days} 
                onChange={(e) => setDays(parseInt(e.target.value))}
                className="w-20"
                min="1"
                max="90"
              />
            </div>
          </div>

          {insights && (
            <>
              {/* Key Metrics */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <Card className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-muted-foreground text-sm">Total Predictions</p>
                    <BarChart3 className="h-5 w-5 text-blue-400" />
                  </div>
                  <p className="text-3xl font-bold">{insights.total_predictions}</p>
                  <p className="text-xs text-muted-foreground mt-1">Last {days} days</p>
                </Card>

                <Card className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-muted-foreground text-sm">Accuracy</p>
                    <Target className="h-5 w-5 text-emerald-400" />
                  </div>
                  <p className="text-3xl font-bold text-emerald-400">{insights.accuracy_pct}%</p>
                  <p className="text-xs text-muted-foreground mt-1">{insights.correct_predictions} correct</p>
                </Card>

                <Card className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-muted-foreground text-sm">Wrong Predictions</p>
                    <AlertTriangle className="h-5 w-5 text-amber-400" />
                  </div>
                  <p className="text-3xl font-bold text-amber-400">{insights.wrong_predictions}</p>
                  <p className="text-xs text-muted-foreground mt-1">Learning from failures</p>
                </Card>

                <Card className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-muted-foreground text-sm">Predicted ↑ Went ↓</p>
                    <XCircle className="h-5 w-5 text-red-400" />
                  </div>
                  <p className="text-3xl font-bold text-red-400">{insights.predicted_up_went_down}</p>
                  <p className="text-xs text-muted-foreground mt-1">Critical failures</p>
                </Card>

                <Card className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-muted-foreground text-sm">Predicted ↓ Went ↑</p>
                    <XCircle className="h-5 w-5 text-red-400" />
                  </div>
                  <p className="text-3xl font-bold text-red-400">{insights.predicted_down_went_up}</p>
                  <p className="text-xs text-muted-foreground mt-1">Critical failures</p>
                </Card>
              </div>

              {/* Top Failure Reasons */}
              <Card className="p-6">
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-amber-400" />
                  Top Failure Patterns
                </h3>
                {insights.top_failure_reasons && insights.top_failure_reasons.length > 0 ? (
                  <div className="space-y-3">
                    {insights.top_failure_reasons.map((failure, idx) => (
                      <div key={idx} className="flex items-center justify-between">
                        <div className="flex items-center gap-3 flex-1">
                          <Badge variant="outline" className="min-w-[40px] justify-center">
                            {idx + 1}
                          </Badge>
                          <span className="font-medium">{formatFailureReason(failure.reason)}</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="w-48 bg-muted rounded-full h-2 overflow-hidden">
                            <div 
                              className="h-full bg-red-400"
                              style={{width: `${(failure.count / insights.wrong_predictions * 100)}%`}}
                            />
                          </div>
                          <span className="text-sm text-muted-foreground min-w-[60px] text-right">
                            {failure.count} cases
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted-foreground text-center py-8">No failure data yet</p>
                )}
              </Card>
            </>
          )}
        </TabsContent>

        {/* Failure Analysis Tab */}
        <TabsContent value="failures" className="space-y-6">
          <h2 className="text-xl font-semibold">Detailed Failure Analysis</h2>
          
          {failureAnalysis && (
            <>
              <Card className="p-6">
                <h3 className="text-lg font-semibold mb-4">Failure Reasons Breakdown</h3>
                <div className="space-y-4">
                  {Object.entries(failureAnalysis.failure_reasons || {}).map(([reason, data]) => (
                    <div key={reason} className="border-l-4 border-red-400 pl-4">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-semibold">{formatFailureReason(reason)}</h4>
                        <Badge>{data.count} occurrences</Badge>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {Object.entries(data.sectors).map(([sector, count]) => (
                          <Badge key={sector} variant="outline">
                            {sector}: {count}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </Card>

              <Card className="p-6">
                <h3 className="text-lg font-semibold mb-4">Sector-wise Failure Count</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {Object.entries(failureAnalysis.sector_failures || {})
                    .sort(([,a], [,b]) => b - a)
                    .map(([sector, count]) => (
                      <div key={sector} className="p-3 border rounded">
                        <p className="font-medium">{sector}</p>
                        <p className="text-2xl font-bold text-red-400">{count}</p>
                      </div>
                    ))}
                </div>
              </Card>
            </>
          )}
        </TabsContent>

        {/* Sector Performance Tab */}
        <TabsContent value="sectors" className="space-y-6">
          <h2 className="text-xl font-semibold">Sector Performance Tracking</h2>
          
          {sectorPerformance && (
            <>
              <Card className="p-6">
                <h3 className="text-lg font-semibold mb-4">Rolling Metrics (3-Day, 5-Day, 7-Day)</h3>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left p-3">Sector</th>
                        <th className="text-right p-3">Period</th>
                        <th className="text-right p-3">Total Predictions</th>
                        <th className="text-right p-3">Success Rate</th>
                        <th className="text-right p-3">Consistency</th>
                        <th className="text-right p-3">Not Successful</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sectorPerformance.rolling_metrics.map((metric, idx) => (
                        <tr key={idx} className="border-b hover:bg-muted/50">
                          <td className="p-3 font-medium">{metric.sector}</td>
                          <td className="p-3 text-right">{metric.rolling_days}d</td>
                          <td className="p-3 text-right">{metric.total_predictions}</td>
                          <td className="p-3 text-right">
                            <Badge className={metric.avg_accuracy_pct >= 70 ? "bg-emerald-500" : metric.avg_accuracy_pct >= 50 ? "bg-amber-500" : "bg-red-500"}>
                              {metric.avg_accuracy_pct}%
                            </Badge>
                          </td>
                          <td className="p-3 text-right">{metric.avg_consistency_score}%</td>
                          <td className="p-3 text-right text-red-400">{metric.wrong_direction || 0}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            </>
          )}
        </TabsContent>

        {/* Recommendations Tab */}
        <TabsContent value="recommendations" className="space-y-6">
          <h2 className="text-xl font-semibold">Dynamic Stock Allocation Recommendations</h2>
          
          {recommendations && (
            <Card className="p-6">
              <div className="mb-4 flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold">Eligible Sectors for Extra Stocks</h3>
                  <p className="text-sm text-muted-foreground">
                    Based on {settings?.sector_success_threshold}% success threshold
                  </p>
                </div>
                <Badge variant="outline" className="text-lg px-4 py-2">
                  {recommendations.total_eligible_sectors} sectors
                </Badge>
              </div>

              {recommendations.recommendations && recommendations.recommendations.length > 0 ? (
                <div className="space-y-4">
                  {recommendations.recommendations.map((rec, idx) => (
                    <div key={idx} className="border rounded-lg p-4 bg-emerald-500/10 border-emerald-500/30">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="text-lg font-semibold flex items-center gap-2">
                          <CheckCircle2 className="h-5 w-5 text-emerald-400" />
                          {rec.sector}
                        </h4>
                        <Badge className="bg-emerald-500 text-lg px-3 py-1">
                          +{rec.recommended_extra_stocks} stocks
                        </Badge>
                      </div>
                      
                      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                        <div>
                          <p className="text-xs text-muted-foreground">7-Day Success Rate</p>
                          <p className="text-2xl font-bold text-emerald-400">{rec.accuracy_7d}%</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Consistency</p>
                          <p className="text-2xl font-bold">{rec.consistency_7d}%</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Sample Size</p>
                          <p className="text-2xl font-bold">{rec.total_predictions_7d}</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Not Successful</p>
                          <p className="text-2xl font-bold text-red-400">{rec.wrong_direction_pct || 0}%</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Today Success</p>
                          <p className="text-2xl font-bold">{rec.today_accuracy}%</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Activity className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
                  <p className="text-muted-foreground">
                    No sectors meet the criteria for extra stock allocation yet.
                  </p>
                  <p className="text-sm text-muted-foreground mt-2">
                    Sectors need ≥{settings?.sector_success_threshold}% accuracy with ≥{settings?.min_sample_size} predictions.
                  </p>
                </div>
              )}
            </Card>
          )}
        </TabsContent>

        {/* Settings Tab */}
        <TabsContent value="settings" className="space-y-6">
          <h2 className="text-xl font-semibold">Learning Engine Configuration</h2>
          
          {settings && (
            <Card className="p-6">
              <div className="space-y-6">
                {/* Enable/Disable Switches */}
                <div className="flex items-center justify-between py-3 border-b">
                  <div>
                    <Label htmlFor="enable_learning">Enable Learning Engine</Label>
                    <p className="text-sm text-muted-foreground">
                      Store predictions and outcomes for ML learning
                    </p>
                  </div>
                  <Switch 
                    id="enable_learning"
                    checked={settings.enable_learning}
                    onCheckedChange={(checked) => updateSettings({enable_learning: checked})}
                  />
                </div>

                <div className="flex items-center justify-between py-3 border-b">
                  <div>
                    <Label htmlFor="enable_allocation">Enable Dynamic Allocation</Label>
                    <p className="text-sm text-muted-foreground">
                      Recommend extra stocks from strong sectors
                    </p>
                  </div>
                  <Switch 
                    id="enable_allocation"
                    checked={settings.enable_dynamic_allocation}
                    onCheckedChange={(checked) => updateSettings({enable_dynamic_allocation: checked})}
                  />
                </div>

                {/* Threshold Settings */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4">
                  <div>
                    <Label htmlFor="success_threshold">Sector Success Threshold (%)</Label>
                    <Input 
                      id="success_threshold"
                      type="number"
                      value={settings.sector_success_threshold}
                      onChange={(e) => updateSettings({sector_success_threshold: parseInt(e.target.value)})}
                      min="50"
                      max="100"
                      className="mt-2"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Minimum accuracy to recommend extra stocks
                    </p>
                  </div>

                  <div>
                    <Label htmlFor="min_sample">Minimum Sample Size</Label>
                    <Input 
                      id="min_sample"
                      type="number"
                      value={settings.min_sample_size}
                      onChange={(e) => updateSettings({min_sample_size: parseInt(e.target.value)})}
                      min="5"
                      max="50"
                      className="mt-2"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Minimum predictions needed per sector
                    </p>
                  </div>

                  <div>
                    <Label htmlFor="wrong_threshold">Max Not Successful (%)</Label>
                    <Input 
                      id="wrong_threshold"
                      type="number"
                      value={settings.wrong_direction_threshold}
                      onChange={(e) => updateSettings({wrong_direction_threshold: parseInt(e.target.value)})}
                      min="0"
                      max="50"
                      className="mt-2"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Maximum allowed unsuccessful predictions
                    </p>
                  </div>

                  <div>
                    <Label htmlFor="consistency_threshold">Consistency Threshold (%)</Label>
                    <Input 
                      id="consistency_threshold"
                      type="number"
                      value={settings.consistency_threshold}
                      onChange={(e) => updateSettings({consistency_threshold: parseInt(e.target.value)})}
                      min="0"
                      max="100"
                      className="mt-2"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Minimum consistency score required
                    </p>
                  </div>

                  <div>
                    <Label htmlFor="extra_min">Extra Stocks (Min)</Label>
                    <Input 
                      id="extra_min"
                      type="number"
                      value={settings.extra_stocks_min}
                      onChange={(e) => updateSettings({extra_stocks_min: parseInt(e.target.value)})}
                      min="1"
                      max="5"
                      className="mt-2"
                    />
                  </div>

                  <div>
                    <Label htmlFor="extra_max">Extra Stocks (Max)</Label>
                    <Input 
                      id="extra_max"
                      type="number"
                      value={settings.extra_stocks_max}
                      onChange={(e) => updateSettings({extra_stocks_max: parseInt(e.target.value)})}
                      min="1"
                      max="10"
                      className="mt-2"
                    />
                  </div>
                </div>
              </div>
            </Card>
          )}
        </TabsContent>
        

        {/* ML Analysis Stocks Tab */}
        <TabsContent value="ml-stocks" className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold">ML Analysis Stocks (Job 19)</h2>
              <p className="text-sm text-muted-foreground">
                20 stocks selected daily for sector performance tracking (no filters applied)
              </p>
            </div>
            <Button 
              onClick={async () => {
                try {
                  setLoading(true);
                  toast.info("Evaluating pending ML stocks...");
                  const res = await axios.post(`${BACKEND_URL}/api/ai/ml-analysis/evaluate-pending`);
                  toast.success(`${res.data.message} - ${res.data.evaluated} stocks evaluated`);
                  await fetchMLAnalysisStocks();
                } catch (err) {
                  toast.error("Failed to evaluate ML stocks");
                } finally {
                  setLoading(false);
                }
              }}
              disabled={loading}
              className="gap-2"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
              Evaluate Pending Stocks
            </Button>
          </div>

          {/* Sector Performance from ML Analysis */}
          {mlSectorPerformance && mlSectorPerformance.status === "completed" && (
            <Card className="p-6">
              <h3 className="text-lg font-semibold mb-4">Sector Performance (Last {days} Days)</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Winning Sectors */}
                <div>
                  <h4 className="text-sm font-medium text-green-500 mb-3">✓ Top Performing Sectors</h4>
                  <div className="space-y-2">
                    {mlSectorPerformance.sector_performance.filter(s => s.avg_return_pct > 0).slice(0, 5).map((sector, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-green-500/10 rounded-lg border border-green-500/30">
                        <div>
                          <div className="font-medium">{sector.sector}</div>
                          <div className="text-xs text-muted-foreground">
                            {sector.wins}/{sector.total_stocks} wins ({sector.win_rate}%)
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-green-500 font-semibold">+{sector.avg_return_pct}%</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Losing Sectors */}
                <div>
                  <h4 className="text-sm font-medium text-red-500 mb-3">✗ Worst Performing Sectors</h4>
                  <div className="space-y-2">
                    {mlSectorPerformance.sector_performance.filter(s => s.avg_return_pct < 0).slice(-5).reverse().map((sector, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-red-500/10 rounded-lg border border-red-500/30">
                        <div>
                          <div className="font-medium">{sector.sector}</div>
                          <div className="text-xs text-muted-foreground">
                            {sector.losses}/{sector.total_stocks} losses ({(100 - sector.win_rate).toFixed(0)}%)
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-red-500 font-semibold">{sector.avg_return_pct}%</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Summary Stats */}
              <div className="mt-6 pt-6 border-t grid grid-cols-4 gap-4">
                <div className="text-center">
                  <div className="text-2xl font-bold">{mlSectorPerformance.total_stocks}</div>
                  <div className="text-sm text-muted-foreground">Total Stocks Analyzed</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold">{mlSectorPerformance.sectors_analyzed}</div>
                  <div className="text-sm text-muted-foreground">Sectors</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-500">{mlSectorPerformance.winning_sectors?.length || 0}</div>
                  <div className="text-sm text-muted-foreground">Winning Sectors</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-red-500">{mlSectorPerformance.losing_sectors?.length || 0}</div>
                  <div className="text-sm text-muted-foreground">Losing Sectors</div>
                </div>
              </div>
            </Card>
          )}

          {/* ML Analysis Stocks Table */}
          {mlAnalysisStocks && mlAnalysisStocks.stocks && (
            <Card className="p-6">
              <h3 className="text-lg font-semibold mb-4">ML Analysis Stocks ({mlAnalysisStocks.total})</h3>
              
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left p-3">Symbol</th>
                      <th className="text-left p-3">Sector</th>
                      <th className="text-left p-3">Strategy</th>
                      <th className="text-right p-3">Score</th>
                      <th className="text-right p-3">Entry</th>
                      <th className="text-right p-3">Target</th>
                      <th className="text-right p-3">Actual</th>
                      <th className="text-center p-3">Hit?</th>
                      <th className="text-left p-3">Trade Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {mlAnalysisStocks.stocks.map((stock, idx) => {
                      const hasActual = stock.actual_price != null;
                      const returnPct = hasActual && stock.start_price 
                        ? (((stock.actual_price - stock.start_price) / stock.start_price) * 100).toFixed(2)
                        : null;
                      
                      return (
                        <tr key={idx} className="border-b hover:bg-muted/50">
                          <td className="p-3 font-medium">{stock.symbol.replace('.NS', '')}</td>
                          <td className="p-3 text-sm">{stock.sector}</td>
                          <td className="p-3">
                            <Badge className={stock.strategy === 'BUY' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'}>
                              {stock.strategy}
                            </Badge>
                          </td>
                          <td className="p-3 text-right">{stock.confidence_score.toFixed(1)}</td>
                          <td className="p-3 text-right">₹{stock.start_price?.toFixed(2) || '—'}</td>
                          <td className="p-3 text-right">₹{stock.predicted_price?.toFixed(2) || '—'}</td>
                          <td className="p-3 text-right">
                            {hasActual ? (
                              <span className={returnPct > 0 ? 'text-green-500' : 'text-red-500'}>
                                ₹{stock.actual_price.toFixed(2)} ({returnPct > 0 ? '+' : ''}{returnPct}%)
                              </span>
                            ) : '—'}
                          </td>
                          <td className="p-3 text-center">
                            {stock.target_hit ? (
                              <Badge className="bg-green-500/20 text-green-400">✓</Badge>
                            ) : hasActual ? (
                              <Badge className="bg-red-500/20 text-red-400">✗</Badge>
                            ) : (
                              <Badge variant="outline">Pending</Badge>
                            )}
                          </td>
                          <td className="p-3 text-sm">{stock.trade_date}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </Card>
          )}

          {!mlAnalysisStocks && (
            <Card className="p-6">
              <div className="text-center text-muted-foreground">
                <Loader2 className="h-8 w-8 animate-spin mx-auto mb-2" />
                Loading ML analysis stocks...
              </div>
            </Card>
          )}
        </TabsContent>

        {/* Learned Patterns Tab (NEW!) */}
        <TabsContent value="learned-patterns" className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold">Auto-Learned Patterns & Rules</h2>
              <p className="text-sm text-muted-foreground">System learns from failures and generates rules automatically</p>
            </div>
            <Button 
              onClick={runAdvancedLearning} 
              disabled={runningAdvancedLearning}
              className="gap-2"
            >
              {runningAdvancedLearning ? (
                <><Loader2 className="h-4 w-4 animate-spin" /> Analyzing...</>
              ) : (
                <><Brain className="h-4 w-4" /> Run Learning Now</>
              )}
            </Button>
          </div>
          
          {/* Learning Summary */}
          {learnedPatterns?.latest_analysis && (
            <Card className="p-6 bg-gradient-to-br from-purple-500/10 to-blue-500/10 border-purple-500/20">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Total Failures Analyzed</p>
                  <p className="text-2xl font-bold">{learnedPatterns.latest_analysis.total_failures_analyzed}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Patterns Discovered</p>
                  <p className="text-2xl font-bold text-blue-400">
                    {learnedPatterns.latest_analysis.news_patterns_count + 
                     learnedPatterns.latest_analysis.sector_patterns_count + 
                     learnedPatterns.latest_analysis.resource_patterns_count}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Rules Generated</p>
                  <p className="text-2xl font-bold text-green-400">{learnedPatterns.latest_analysis.rules_generated}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Last Updated</p>
                  <p className="text-sm font-medium">
                    {new Date(learnedPatterns.latest_analysis.analysis_date).toLocaleString()}
                  </p>
                </div>
              </div>
            </Card>
          )}
          
          {/* Active Rules */}
          {learnedRules?.rules_by_type && (
            <Card className="p-6">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-green-500" />
                Active Rules ({learnedRules.active_rules})
              </h3>
              
              {Object.keys(learnedRules.rules_by_type).length === 0 ? (
                <p className="text-muted-foreground text-center py-8">
                  No rules learned yet. Click "Run Learning Now" to analyze failures and generate rules.
                </p>
              ) : (
                <div className="space-y-6">
                  {Object.entries(learnedRules.rules_by_type).map(([ruleType, rules]) => (
                    <div key={ruleType} className="space-y-3">
                      <div className="flex items-center justify-between">
                        <h4 className="font-medium text-sm uppercase tracking-wide text-muted-foreground">
                          {ruleType.replace(/_/g, ' ')}
                        </h4>
                        <Badge variant="outline">{rules.length} rules</Badge>
                      </div>
                      
                      <div className="space-y-2">
                        {rules.slice(0, 5).map((rule, idx) => (
                          <div 
                            key={idx}
                            className="p-3 rounded-lg border bg-card/50 hover:bg-card transition-colors"
                          >
                            <div className="flex items-start justify-between gap-4">
                              <div className="flex-1">
                                <p className="text-sm font-medium">{rule.reason || 'Auto-generated rule'}</p>
                                <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                                  {rule.condition?.sector && (
                                    <span>Sector: <span className="font-medium text-foreground">{rule.condition.sector}</span></span>
                                  )}
                                  {rule.condition?.strategy && (
                                    <span>Strategy: <span className="font-medium text-foreground">{rule.condition.strategy}</span></span>
                                  )}
                                  {rule.condition?.event_detected && (
                                    <span>Event: <span className="font-medium text-foreground">{rule.condition.event_detected}</span></span>
                                  )}
                                </div>
                              </div>
                              <div className="text-right">
                                <Badge 
                                  variant={rule.action === 'REDUCE_SCORE' ? 'destructive' : 'default'}
                                  className="text-xs"
                                >
                                  {rule.action === 'REDUCE_SCORE' ? '-' : '+'}{rule.score_adjustment}%
                                </Badge>
                                <p className="text-xs text-muted-foreground mt-1">
                                  {(rule.confidence * 100).toFixed(0)}% confidence
                                </p>
                              </div>
                            </div>
                          </div>
                        ))}
                        {rules.length > 5 && (
                          <p className="text-xs text-muted-foreground text-center py-2">
                            ... and {rules.length - 5} more rules
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          )}
          
          {/* How It Works */}
          <Card className="p-6 bg-gradient-to-r from-amber-500/10 to-orange-500/10 border-amber-500/20">
            <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
              <Activity className="h-5 w-5 text-amber-500" />
              How Auto-Learning Works
            </h3>
            <div className="space-y-2 text-sm text-muted-foreground">
              <p><strong>1. Failure Analysis:</strong> System analyzes all unsuccessful predictions (losses/break-even)</p>
              <p><strong>2. News Correlation:</strong> Correlates failures with news events (e.g., "Middle East unrest → Oil sector stocks failed")</p>
              <p><strong>3. Pattern Detection:</strong> Discovers sector biases and resource dependencies</p>
              <p><strong>4. Rule Generation:</strong> Automatically creates rules like "IF oil_crisis AND aviation_sector → REDUCE score by 25%"</p>
              <p><strong>5. Auto-Application:</strong> These rules are automatically applied in tomorrow's stock selection (Job 9)</p>
              <p className="text-amber-500 font-medium mt-3">💡 Example: If system learns that Oil sector fails during Middle East unrest, it will automatically reduce scores for Aviation, Chemicals, and other oil-dependent sectors when such news is detected.</p>
            </div>
          </Card>
          
          {/* Sector Performance (NEW!) */}
          {sectorPerformanceDetailed?.sector_analysis && sectorPerformanceDetailed.sector_analysis.length > 0 && (
            <Card className="p-6">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <PieChart className="h-5 w-5 text-blue-500" />
                Sector Performance Analysis
              </h3>
              
              <div className="mb-4 flex items-center justify-between">
                <div className="text-sm text-muted-foreground">
                  Analyzed {sectorPerformanceDetailed.total_predictions_analyzed} predictions over last {sectorPerformanceDetailed.lookback_days} days
                </div>
                <div className="flex items-center gap-4">
                  {sectorPerformanceDetailed.blocked_sectors?.length > 0 && (
                    <Badge variant="destructive" className="gap-1">
                      <XCircle className="h-3 w-3" />
                      {sectorPerformanceDetailed.blocked_sectors.length} Blocked
                    </Badge>
                  )}
                  {sectorPerformanceDetailed.preferred_sectors?.length > 0 && (
                    <Badge variant="default" className="bg-green-500 gap-1">
                      <CheckCircle2 className="h-3 w-3" />
                      {sectorPerformanceDetailed.preferred_sectors.length} Preferred
                    </Badge>
                  )}
                </div>
              </div>
              
              {/* Blocked Sectors Alert */}
              {sectorPerformanceDetailed.blocked_sectors?.length > 0 && (
                <div className="mb-4 p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
                  <h4 className="font-semibold text-red-500 flex items-center gap-2 mb-2">
                    <AlertTriangle className="h-4 w-4" />
                    Blocked Sectors - NO stocks will be selected from these sectors
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {sectorPerformanceDetailed.blocked_sectors.map(sector => (
                      <Badge key={sector} variant="destructive">{sector}</Badge>
                    ))}
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">
                    Blocking Criteria: {sectorPerformanceDetailed.criteria?.block_threshold}
                  </p>
                </div>
              )}
              
              {/* Sector Table */}
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left p-2">Sector</th>
                      <th className="text-right p-2">Total</th>
                      <th className="text-right p-2">Success Rate</th>
                      <th className="text-right p-2">Failure Rate</th>
                      <th className="text-right p-2">Avg Probability</th>
                      <th className="text-right p-2">Win Rate</th>
                      <th className="text-center p-2">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sectorPerformanceDetailed.sector_analysis.slice(0, 15).map((sector, idx) => {
                      const isBlocked = sectorPerformanceDetailed.blocked_sectors?.includes(sector.sector);
                      const isPreferred = sectorPerformanceDetailed.preferred_sectors?.includes(sector.sector);
                      
                      return (
                        <tr 
                          key={idx} 
                          className={`border-b hover:bg-card/50 ${
                            isBlocked ? 'bg-red-500/5' : isPreferred ? 'bg-green-500/5' : ''
                          }`}
                        >
                          <td className="p-2 font-medium">{sector.sector}</td>
                          <td className="p-2 text-right text-muted-foreground">{sector.total_predictions}</td>
                          <td className={`p-2 text-right font-mono ${
                            sector.success_rate >= 65 ? 'text-green-500' : 
                            sector.success_rate >= 50 ? 'text-yellow-500' : 'text-red-500'
                          }`}>
                            {sector.success_rate.toFixed(1)}%
                          </td>
                          <td className={`p-2 text-right font-mono ${
                            sector.failure_rate >= 70 ? 'text-red-500 font-bold' : 
                            sector.failure_rate >= 50 ? 'text-yellow-500' : 'text-green-500'
                          }`}>
                            {sector.failure_rate.toFixed(1)}%
                          </td>
                          <td className={`p-2 text-right font-mono ${
                            sector.avg_probability >= 60 ? 'text-green-500' : 
                            sector.avg_probability < 50 ? 'text-red-500' : 'text-yellow-500'
                          }`}>
                            {sector.avg_probability.toFixed(1)}%
                          </td>
                          <td className="p-2 text-right font-mono text-muted-foreground">
                            {sector.win_rate.toFixed(1)}%
                          </td>
                          <td className="p-2 text-center">
                            {isBlocked && <Badge variant="destructive" className="text-xs">BLOCKED</Badge>}
                            {isPreferred && <Badge variant="default" className="bg-green-500 text-xs">PREFERRED</Badge>}
                            {!isBlocked && !isPreferred && <Badge variant="outline" className="text-xs">NEUTRAL</Badge>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              
              {sectorPerformanceDetailed.sector_analysis.length > 15 && (
                <p className="text-xs text-muted-foreground text-center mt-4">
                  Showing top 15 of {sectorPerformanceDetailed.sector_analysis.length} sectors analyzed
                </p>
              )}
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
